import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from 'react';
import { Icon, type IconName } from '../icons';
import { cx } from './cx';
import { Tooltip } from './Tooltip';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'danger';
export type ButtonSize = 'md' | 'sm';

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'btn-p',
  secondary: '',
  ghost: 'btn-g',
  destructive: 'btn-d',
  danger: 'btn-dd',
};

/** Class names of a button; use for router links styled as buttons. */
export function buttonClass(
  opts: { variant?: ButtonVariant; size?: ButtonSize; iconOnly?: boolean; className?: string } = {},
): string {
  return cx(
    'btn',
    VARIANT[opts.variant ?? 'secondary'],
    opts.size === 'sm' && 'sm',
    opts.iconOnly && 'ib',
    opts.className,
  );
}

export type ButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading icon. */
  icon?: IconName;
  iconRight?: IconName;
  /** Icon-only button: `label` becomes the aria-label. */
  iconOnly?: boolean;
  label?: string;
  loading?: boolean;
  /** Keyboard hint rendered after the label, e.g. "⌘↵" (icon-only buttons show it in the tooltip). */
  shortcut?: string;
  /** Icon-only buttons show `label` as a styled tooltip; `false` turns it off. */
  tooltip?: boolean;
  children?: ReactNode;
};

/** Button variants from the style guide: Primary, Secondary, Ghost, Destructive, Delete (danger). */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    icon,
    iconRight,
    iconOnly,
    label,
    loading,
    shortcut,
    className,
    children,
    type = 'button',
    disabled,
    tooltip = true,
    ...rest
  },
  ref,
) {
  const button = (
    <button
      ref={ref}
      type={type}
      className={buttonClass({ variant, size, iconOnly, className })}
      aria-label={iconOnly ? label : rest['aria-label']}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? (
        <span className="spinner" aria-hidden="true" style={{ width: 14, height: 14 }} />
      ) : icon ? (
        <Icon name={icon} size={size === 'sm' ? 15 : 16} />
      ) : null}
      {iconOnly ? null : children}
      {shortcut && !iconOnly ? <span className="kbd-hint">{shortcut}</span> : null}
      {iconRight ? <Icon name={iconRight} size={14} /> : null}
    </button>
  );
  // Icon-only buttons get a styled tooltip from their label (never a native `title`).
  if (iconOnly && label && tooltip && !rest.title)
    return (
      <Tooltip content={label} shortcut={shortcut}>
        {button}
      </Tooltip>
    );
  return button;
});
